import { useState, useEffect, useRef } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { Plus, Pencil, History, PowerOff, X, Truck, Package, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { findVinConflict } from '@/lib/vinCheck';
import { TRUCK_TYPES, TRAILER_TYPES } from '@/lib/equipmentTypes';

const EXPIRY_CLASS = (dateStr) => {
  if (!dateStr) return 'text-muted-foreground';
  const days = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
  if (days <= 0) return 'text-red-400 font-semibold';
  if (days <= 30) return 'text-yellow-400 font-semibold';
  return 'text-green-400';
};

const emptyAsset = () => ({
  unit_number: '', asset_type: 'Truck', year: '', make: '', model: '',
  vin: '', license_plate: '', plate_state: '', plate_expiration: '',
  registration_expiration: '', annual_inspection_due: '',
  ownership_type: 'Owned', assigned_driver_email: '',
  current_odometer: 0, is_active: true, notes: '',
  // Day Cab/Sleeper/etc. (Truck) or Dry Van/Reefer/etc. (Trailer) -- a
  // property of the physical unit, synced onto whichever Company Driver
  // is assigned it below (handleSave) so their profile always reflects
  // their actual equipment instead of a stale/default value.
  truck_type: '', trailer_type: '', trailer_size: '',
});

export default function AssetsTab({ alerts = [], onViewHistory, editAssetId }) {
  const openedTargetRef = useRef(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editAsset, setEditAsset] = useState(null);
  const [form, setForm] = useState(emptyAsset());
  const [saving, setSaving] = useState(false);
  const [drivers, setDrivers] = useState([]);

  useEffect(() => {
    loadAssets();
    client.entities.DriverProfile.filter({ is_active: true }, 'full_name', 100)
      .then(setDrivers).catch(() => {});
  }, []);

  const loadAssets = async () => {
    setLoading(true);
    const data = await client.entities.Asset.list('-created_date', 200);
    setAssets(data);
    setLoading(false);
  };

  const openAdd = (type) => {
    setEditAsset(null);
    setForm({ ...emptyAsset(), asset_type: type });
    setShowModal(true);
  };

  const openEdit = (asset) => {
    setEditAsset(asset);
    setForm({ ...emptyAsset(), ...asset });
    setShowModal(true);
  };

  // Auto-open a specific asset's editor when navigated here from an alert
  useEffect(() => {
    if (!editAssetId || openedTargetRef.current === editAssetId || assets.length === 0) return;
    const asset = assets.find(a => a.id === editAssetId);
    if (asset) {
      openedTargetRef.current = editAssetId;
      openEdit(asset);
    }
  }, [editAssetId, assets]);

  const handleSave = async () => {
    if (!form.unit_number?.trim()) { toast.error('Unit number is required.'); return; }
    setSaving(true);
    // Previously had no try/catch at all -- a failure anywhere below
    // (VIN conflict check aside, the save itself) threw and left
    // setSaving(true) stuck forever with zero feedback: the button just
    // sat on "Saving..." indefinitely with no error shown, no way to
    // retry short of closing the modal. Same failure mode already fixed
    // on Load save (NewLoadModal/EditLoadModal's handleCreate/handleSave).
    try {
      // Clear stale synced equipment info from whoever was PREVIOUSLY
      // assigned, before anything else, whenever this save changes or
      // removes the assignment -- unassigning a driver, or reassigning
      // this exact asset to someone else, previously left
      // truck_info/truck_vin/license_plate (or trailer_info/trailer_vin)
      // on the OLD driver's profile forever. Beyond showing a
      // permanently-stale truck on their own profile page and
      // documents, this made findVinConflict below see that stale VIN
      // as still "belonging" to the old driver and block reassigning it
      // to the new one -- clearing it FIRST, before the VIN check runs,
      // fixes that deadlock instead of just working around it.
      const prevAssignedEmail = editAsset?.assigned_driver_email || '';
      const nextAssignedEmail = form.assigned_driver_email || '';
      if (prevAssignedEmail && prevAssignedEmail.toLowerCase() !== nextAssignedEmail.toLowerCase()) {
        const prevProfiles = await client.entities.DriverProfile.filter({ user_email: prevAssignedEmail }).catch(() => []);
        if (prevProfiles.length > 0) {
          const prevProfile = prevProfiles[0];
          const clearData = form.asset_type === 'Truck'
            ? { truck_info: '', truck_vin: '', license_plate: '' }
            : { trailer_info: '', trailer_vin: '' };
          // truck_type/trailer_type is only carrier-managed (synced from
          // Assets) for a Company Driver -- an Owner Operator's own
          // truck_type is self-reported in DriverFormModal and shouldn't
          // be touched just because THIS asset's assignment changed.
          if (prevProfile.driver_type === 'Company Driver') {
            if (form.asset_type === 'Truck') clearData.truck_type = '';
            else { clearData.trailer_type = ''; clearData.trailer_size = ''; }
          }
          await client.entities.DriverProfile.update(prevProfile.id, clearData);
        }
      }
      // Resolved up front so the VIN check can exclude this asset's own
      // assigned driver -- the save below syncs this exact VIN onto that
      // driver's profile, so on every re-save after the first, that profile
      // legitimately already carries it. Without excluding it, findVinConflict
      // saw that as a foreign conflict and blocked the save.
      const assignedProfiles = nextAssignedEmail
        ? await client.entities.DriverProfile.filter({ user_email: nextAssignedEmail }).catch(() => [])
        : [];
      if (form.vin) {
        const vinConflict = await findVinConflict(form.vin, { excludeAssetId: editAsset?.id, excludeProfileId: assignedProfiles[0]?.id });
        if (vinConflict) { toast.error(vinConflict); return; }
      }
      // plate_expiration/registration_expiration/annual_inspection_due are
      // real `date`-typed columns -- Postgres rejects an empty string with
      // a 400, same class of bug already fixed on pickup_date/delivery_date
      // for loads. These three default to '' in emptyAsset() and are
      // commonly left blank (not every unit has all three tracked), so
      // this was the most likely way to hit the silent-stuck-save above.
      const payload = {
        ...form,
        plate_expiration: form.plate_expiration || null,
        registration_expiration: form.registration_expiration || null,
        annual_inspection_due: form.annual_inspection_due || null,
      };
      if (editAsset) {
        await client.entities.Asset.update(editAsset.id, payload);
        toast.success('Asset updated.');
      } else {
        await client.entities.Asset.create(payload);
        toast.success('Asset added.');
      }
      // Sync equipment info to the assigned driver profile
      if (nextAssignedEmail) {
        if (assignedProfiles.length > 0) {
          const assignedProfile = assignedProfiles[0];
          const updateData = {};
          if (form.asset_type === 'Truck') {
            updateData.truck_info = `${form.year || ''} ${form.make || ''} ${form.model || ''}`.trim();
            updateData.truck_vin = form.vin || '';
            updateData.license_plate = form.license_plate || '';
            // Day Cab/Sleeper/etc. -- see the driver_type guard note above.
            if (assignedProfile.driver_type === 'Company Driver') {
              updateData.truck_type = form.truck_type || '';
            }
          }
          if (form.asset_type === 'Trailer') {
            updateData.trailer_info = `${form.year || ''} ${form.make || ''} ${form.model || ''}`.trim();
            // Truck sync includes truck_vin above -- trailer sync never
            // synced trailer_vin at all, leaving it permanently blank
            // (or stale from before) regardless of what trailer was
            // actually assigned.
            updateData.trailer_vin = form.vin || '';
            if (assignedProfile.driver_type === 'Company Driver') {
              updateData.trailer_type = form.trailer_type || '';
              // Feeds NewLoadModal.jsx's auto-fill of Equipment/Trailer Size
              // when this driver is picked for a new load.
              updateData.trailer_size = form.trailer_size || '';
            }
          }
          await client.entities.DriverProfile.update(assignedProfile.id, updateData);
        }
      }
      setShowModal(false);
      loadAssets();
    } catch (err) {
      toast.error(err?.message || 'Failed to save asset. Please try again.', { duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (asset) => {
    if (!window.confirm(`${asset.is_active ? 'Deactivate' : 'Reactivate'} Unit ${asset.unit_number}?`)) return;
    await client.entities.Asset.update(asset.id, { is_active: !asset.is_active });
    toast.success(`Unit ${asset.unit_number} ${asset.is_active ? 'deactivated' : 'reactivated'}.`);
    loadAssets();
  };

  const handleDelete = async (asset) => {
    if (!window.confirm(`Permanently delete Unit ${asset.unit_number}? This cannot be undone. Use Deactivate instead if you want to keep its history.`)) return;
    await client.entities.Asset.delete(asset.id);
    toast.success(`Unit ${asset.unit_number} deleted.`);
    loadAssets();
  };

  const filtered = assets.filter(a => {
    if (filter === 'Trucks') return a.asset_type === 'Truck';
    if (filter === 'Trailers') return a.asset_type === 'Trailer';
    if (filter === 'Non-Owned') return a.ownership_type === 'Non-Owned';
    if (filter === 'Active') return a.is_active;
    if (filter === 'Inactive') return !a.is_active;
    return true;
  }).filter(a => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (a.unit_number || '').toLowerCase().includes(q) ||
      (a.vin || '').toLowerCase().includes(q) ||
      (a.license_plate || '').toLowerCase().includes(q);
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="space-y-4">
      {/* Alert banner */}
      {alerts.length > 0 && <MaintenanceAlertBanner alerts={alerts} />}

      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {['All', 'Trucks', 'Trailers', 'Non-Owned', 'Active', 'Inactive'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={cn("text-xs font-semibold px-3 py-1 rounded-full border transition-colors",
                filter === f ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:text-foreground hover:border-border'
              )}>{f}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search unit, VIN, plate..."
            className="text-foreground w-52 text-sm h-8" />
          <Button onClick={() => openAdd('Truck')} size="sm" className="bg-primary text-primary-foreground h-8 text-xs">
            <Plus className="w-3.5 h-3.5 mr-1" /><Truck className="w-3.5 h-3.5 mr-1" /> Truck
          </Button>
          <Button onClick={() => openAdd('Trailer')} size="sm" className="bg-secondary text-foreground h-8 text-xs">
            <Plus className="w-3.5 h-3.5 mr-1" /><Package className="w-3.5 h-3.5 mr-1" /> Trailer
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border">
                {['Unit #','Type','Year/Make/Model','VIN','Plate','Plate Exp','Reg Exp','Inspection Due','Driver','Odometer','Status',''].map(h => (
                  <th key={h} className="text-left px-3 py-2.5 text-muted-foreground font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map(a => (
                <tr key={a.id} className="hover:bg-white/3 transition-colors">
                  <td className="px-3 py-2.5 font-semibold text-foreground">{a.unit_number}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">{a.asset_type}</td>
                  <td className="px-3 py-2.5 text-foreground">{[a.year, a.make, a.model].filter(Boolean).join(' ') || '—'}</td>
                  <td className="px-3 py-2.5 text-muted-foreground font-mono text-[10px]">{a.vin || '—'}</td>
                  <td className="px-3 py-2.5 text-foreground">{a.license_plate ? `${a.license_plate} ${a.plate_state || ''}` : '—'}</td>
                  <td className={cn("px-3 py-2.5", EXPIRY_CLASS(a.plate_expiration))}>{a.plate_expiration || '—'}</td>
                  <td className={cn("px-3 py-2.5", EXPIRY_CLASS(a.registration_expiration))}>{a.registration_expiration || '—'}</td>
                  <td className={cn("px-3 py-2.5", EXPIRY_CLASS(a.annual_inspection_due))}>{a.annual_inspection_due || '—'}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">{drivers.find(d => d.user_email === a.assigned_driver_email)?.full_name || a.assigned_driver_email || '—'}</td>
                  <td className="px-3 py-2.5 text-foreground">{a.current_odometer?.toLocaleString() || '0'}</td>
                  <td className="px-3 py-2.5">
                    <span className={cn("text-[10px] font-semibold px-2 py-0.5 rounded-full border",
                      a.is_active ? 'text-green-700 dark:text-green-400 bg-green-500/15 dark:bg-green-500/10 border-green-500/40 dark:border-green-500/30' : 'text-red-700 dark:text-red-400 bg-red-500/15 dark:bg-red-500/10 border-red-500/40 dark:border-red-500/30'
                    )}>{a.is_active ? 'Active' : 'Inactive'}</span>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5 justify-end">
                      <button onClick={() => openEdit(a)} className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                      {onViewHistory && <button onClick={() => onViewHistory(a)} className="p-1 rounded text-muted-foreground hover:text-blue-400 hover:bg-blue-500/10 transition-colors" title="View History"><History className="w-3.5 h-3.5" /></button>}
                      <button onClick={() => handleDeactivate(a)} className="p-1 rounded text-muted-foreground hover:text-amber-400 hover:bg-amber-500/10 transition-colors" title={a.is_active ? 'Deactivate' : 'Reactivate'}><PowerOff className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleDelete(a)} className="p-1 rounded text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <p className="text-center text-muted-foreground py-8 text-sm">{loading ? 'Loading...' : 'No assets found.'}</p>}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowModal(false)} />
          <div className="flex min-h-full items-start justify-center p-4">
            <div className="relative w-full max-w-2xl my-8 p-6 space-y-4 z-10 rounded-2xl border border-border bg-background">
              <div className="flex items-center justify-between">
                <h2 className="font-space font-semibold text-foreground text-lg">{editAsset ? 'Edit Asset' : `Add ${form.asset_type}`}</h2>
                <button onClick={() => setShowModal(false)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {[
                  ['Unit Number *', 'unit_number'], ['Year', 'year'], ['Make', 'make'],
                  ['Model', 'model'], ['VIN', 'vin'], ['License Plate', 'license_plate'],
                  ['Plate State', 'plate_state'],
                ].map(([label, key]) => (
                  <div key={key}>
                    <label className="text-xs text-muted-foreground block mb-1">{label}</label>
                    <Input value={form[key] || ''} onChange={e => set(key, e.target.value)} className="text-foreground text-sm" />
                  </div>
                ))}
                {[
                  ['Plate Expiration', 'plate_expiration'], ['Registration Expiration', 'registration_expiration'], ['Annual Inspection Due', 'annual_inspection_due'],
                ].map(([label, key]) => (
                  <div key={key}>
                    <label className="text-xs text-muted-foreground block mb-1">{label}</label>
                    <Input type="date" value={form[key] || ''} onChange={e => set(key, e.target.value)} className="text-foreground text-sm" />
                  </div>
                ))}
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Type</label>
                  <select value={form.asset_type} onChange={e => set('asset_type', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm">
                    <option value="Truck" className="bg-background">Truck</option>
                    <option value="Trailer" className="bg-background">Trailer</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">{form.asset_type === 'Trailer' ? 'Trailer Type' : 'Truck Type'}</label>
                  <select
                    value={form.asset_type === 'Trailer' ? (form.trailer_type || '') : (form.truck_type || '')}
                    onChange={e => set(form.asset_type === 'Trailer' ? 'trailer_type' : 'truck_type', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm"
                  >
                    <option value="" className="bg-background">— Not set —</option>
                    {(form.asset_type === 'Trailer' ? TRAILER_TYPES : TRUCK_TYPES).map(t => (
                      <option key={t} value={t} className="bg-background">{t}</option>
                    ))}
                  </select>
                </div>
                {form.asset_type === 'Trailer' && (
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Trailer Size (e.g. 53 ft)</label>
                    <Input value={form.trailer_size || ''} onChange={e => set('trailer_size', e.target.value)} placeholder="53" className="text-foreground text-sm" />
                  </div>
                )}
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Ownership</label>
                  <select value={form.ownership_type} onChange={e => set('ownership_type', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm">
                    {['Owned','Leased','Rented','Non-Owned'].map(o => <option key={o} value={o} className="bg-background">{o}</option>)}
                  </select>
                  {form.ownership_type === 'Non-Owned' && (
                    <p className="text-xs text-muted-foreground mt-1">Owner-operator's own equipment -- not carrier-owned/leased/rented.</p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Assigned Driver</label>
                  <select value={form.assigned_driver_email || ''} onChange={e => set('assigned_driver_email', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm">
                    <option value="" className="bg-background">— None —</option>
                    {drivers.map(d => <option key={d.id} value={d.user_email} className="bg-background">{d.full_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Current Odometer</label>
                  <Input type="number" value={form.current_odometer || 0} onChange={e => set('current_odometer', parseFloat(e.target.value) || 0)} className="text-foreground text-sm" />
                </div>
                <div className="col-span-2 sm:col-span-3">
                  <label className="text-xs text-muted-foreground block mb-1">Notes</label>
                  <textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} rows={2} className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm resize-none" />
                </div>
              </div>

              <div className="flex gap-3 pt-1">
                <Button onClick={handleSave} disabled={saving} className="flex-1 bg-primary text-primary-foreground">{saving ? 'Saving...' : 'Save Asset'}</Button>
                <Button onClick={() => setShowModal(false)} variant="outline" className="border-border">Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MaintenanceAlertBanner({ alerts }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed || alerts.length === 0) return null;
  const critical = alerts.filter(a => a.severity === 'critical');
  const high = alerts.filter(a => a.severity === 'high');
  const medium = alerts.filter(a => a.severity === 'medium');
  return (
    <div className="rounded-xl border border-border overflow-hidden">
      {critical.length > 0 && (
        <div className="bg-red-500/10 border-b border-border px-4 py-3">
          <p className="text-xs font-bold text-red-400 mb-2">🔴 CRITICAL — Overdue</p>
          {critical.map((a, i) => <p key={i} className="text-xs text-red-300">{a.label}{a.date ? ` · ${a.date}` : ''}</p>)}
        </div>
      )}
      {high.length > 0 && (
        <div className="bg-amber-500/10 border-b border-border px-4 py-3">
          <p className="text-xs font-bold text-amber-400 mb-2">🟡 HIGH — Due within 7 days / 500 miles</p>
          {high.map((a, i) => <p key={i} className="text-xs text-amber-300">{a.label}{a.date ? ` · ${a.date}` : ''}</p>)}
        </div>
      )}
      {medium.length > 0 && (
        <div className="bg-blue-500/10 px-4 py-3">
          <p className="text-xs font-bold text-blue-400 mb-2">🔵 MEDIUM — Due within 14–30 days / 1,500 miles</p>
          {medium.map((a, i) => <p key={i} className="text-xs text-blue-300">{a.label}{a.date ? ` · ${a.date}` : ''}</p>)}
        </div>
      )}
      <div className="bg-white/3 px-4 py-2 flex justify-end">
        <button onClick={() => setDismissed(true)} className="text-xs text-muted-foreground hover:text-foreground transition-colors">Dismiss</button>
      </div>
    </div>
  );
}
